import { scopeCollectionPurposeId, isCreativeScenesPurpose, type CollectionPurpose } from '@motionpitch/template-contract';
import type { ContentType, MockupCollection } from '../types';
import { escapeHtml } from './dom';

export function getCollectionPurposeId(collection: MockupCollection | null | undefined, purposes: CollectionPurpose[]): string {
  const type = collection?.templateType?.startsWith('MOTION') ? 'MOTION' : 'STATIC';
  const id=scopeCollectionPurposeId(collection?.purposeId,type);
  return purposes.some(p=>p.id===id && p.contentTypes.includes(type)) ? id : scopeCollectionPurposeId(undefined,type);
}

function isDarkTitleColor(fontColor: string): boolean {
  const match = /^#([0-9a-f]{6})$/i.exec(fontColor);
  if (!match) return false;
  const value = Number.parseInt(match[1], 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue < 128;
}

function cardStyle(background: string, fontColor = '#ffffff', blurredBackground = ''): string {
  const cover = background ? `url(${JSON.stringify(background)})` : 'linear-gradient(120deg, #1f315c, #2a436e)';
  const blurredCover = blurredBackground ? `url(${JSON.stringify(blurredBackground)})` : cover;
  const frost = isDarkTitleColor(fontColor) ? 'rgba(255,255,255,0.32)' : 'rgba(8,16,32,0.1)';
  return `--collection-card-cover: ${cover}; --collection-card-blur-cover: ${blurredCover}; --collection-card-frost: ${frost}; background-image: var(--collection-card-cover)`;
}

function titleClass(fontColor: string): string {
  return isDarkTitleColor(fontColor)
    ? 'collection-card-title is-dark-text'
    : 'collection-card-title';
}

const cardVisualLayers = '<span class="collection-card-cover" aria-hidden="true"></span><span class="collection-card-blur" aria-hidden="true"><span class="collection-card-blur-image"></span></span>';

export function renderCollectionCards(collections: MockupCollection[], count: (id: string)=>number): string {
  return `<div class="collections-list">${collections.filter(c=>c.published!==false&&c.listed!==false).map(c=>{const color=c.autoFontColor||c.fontColor;return `<button type="button" class="collection-card" data-open-collection-id="${escapeHtml(c.id)}" style="${escapeHtml(cardStyle(c.background,color,c.autoBlurBackground))}">
    ${cardVisualLayers}
    <span class="${titleClass(color)}" style="color:${escapeHtml(color)}"><span class="collection-card-name">${escapeHtml(c.name)}</span>
    <span class="collection-card-mobile-meta" aria-label="${count(c.id)} items"><span class="collection-card-mobile-separator" aria-hidden="true">|</span><span class="collection-card-mobile-count">${count(c.id)}</span><span class="collection-card-mobile-unit">items</span></span></span>
    <small>${count(c.id)} templates</small></button>`}).join('')}</div>`;
}

export function renderCollectionHierarchy(options: {
  purposes: CollectionPurpose[]; collections: MockupCollection[]; contentType: ContentType;
  selectedPurposeId: string; query: string; countTemplates: (id:string)=>number;
  matchingTemplateCollectionIds?: string[]; allCollections: MockupCollection[];
  loading?: boolean; error?: string;
}): string {
  if(options.loading)return '<p class="hint" role="status" aria-live="polite">Loading collections…</p>';
  if(options.error)return `<p class="hint" role="alert">${escapeHtml(options.error)}</p>`;
  const {purposes,contentType,selectedPurposeId,query,countTemplates}=options;
  const collections=options.collections.filter(c=>c.published!==false&&c.listed!==false&&
    (c.templateType.startsWith('MOTION')?'MOTION':'STATIC')===contentType&&purposes.some(p=>p.id===getCollectionPurposeId(c,purposes)&&p.published));
  if(selectedPurposeId) {
    const id=scopeCollectionPurposeId(selectedPurposeId,contentType);
    const purpose=purposes.find(p=>p.id===id&&p.published&&p.contentTypes.includes(contentType));
    if(!purpose)return '<p class="hint">This section is unavailable.</p>';
    const children=collections.filter(c=>getCollectionPurposeId(c,purposes)===id);
    return `${purpose.background?`<div class="purpose-banner" style="${escapeHtml(cardStyle(purpose.background))}" aria-hidden="true"></div>`:''}${children.length?renderCollectionCards(children,countTemplates):'<p class="hint">No collections found.</p>'}`;
  }
  const creative=purposes.find(p=>isCreativeScenesPurpose(p.id)&&p.contentTypes.includes(contentType)&&p.published&&p.listed);
  const creativeCollections=collections.filter(c=>getCollectionPurposeId(c,purposes)===creative?.id);
  const utility=purposes.filter(p=>!isCreativeScenesPurpose(p.id)&&p.contentTypes.includes(contentType)&&p.published&&p.listed).filter(p=>!query ||
    p.name.toLowerCase().includes(query.toLowerCase()) || collections.some(c=>getCollectionPurposeId(c,purposes)===p.id) ||
    options.allCollections.some(c=>getCollectionPurposeId(c,purposes)===p.id&&options.matchingTemplateCollectionIds?.includes(c.id)));
  return `<div class="collections-list collection-purpose-grid">${utility.map(p=>{
    const count=options.allCollections.filter(c=>c.published!==false&&c.listed!==false&&(c.templateType.startsWith('MOTION')?'MOTION':'STATIC')===contentType&&getCollectionPurposeId(c,purposes)===p.id).length;
    const color=p.autoFontColor||p.fontColor;
    return `<button type="button" class="collection-card collection-purpose-card" data-open-purpose-id="${escapeHtml(p.id)}" style="${escapeHtml(cardStyle(p.background,color,p.autoBlurBackground))}">
      ${cardVisualLayers}
      <span class="${titleClass(color)}" style="color:${escapeHtml(color)}"><span class="collection-card-name">${escapeHtml(p.name)}</span></span><small>${count} collections</small></button>`;
  }).join('')}</div>
  ${creative?`<section class="creative-scenes-section"><h2>${escapeHtml(creative.name)}</h2>
    ${creativeCollections.length?renderCollectionCards(creativeCollections,countTemplates):'<p class="hint">No collections found.</p>'}</section>`:''}`;
}
